import React from 'react';
import ChipList from 'components/ChipList';
import Chip from 'components/Chip';
import { SkeletonLine } from 'components/theme/Skeleton';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { MainTitle } from 'components/theme/Typography/Typography';
import useLimitedTimeStatus from 'hooks/useLimitedTimeStatus';
import { PublicListingDetailsV3 } from 'types/listingV3';
import LimitedTimeMessage from 'components/atoms/LimitedTime/LimitedTimeMessage';
import { HyattExperienceBenefits } from 'components/molecules/HyattExperienceBenefits';
import {
  ExperiencesHeaderContent,
  BrandName,
  Headline,
  TagButtonWrap,
  CategoryChip,
} from './styles';

interface MainTitlesProps {
  promoterBrand: boolean;
}

export const MainTitles = styled(MainTitle)<MainTitlesProps>`
  margin-bottom: ${({ promoterBrand }) => (promoterBrand ? '12px' : '20px')};
  font-size: 36px;
  line-height: 44px;
  @media (min-width: 768px) {
    margin-top: 10px;
    font-size: 36px;
    line-height: 44px;
  }
`;

interface Vibe {
  name: string;
}

interface Category {
  name: string;
}

interface IExperience {
  title?: string;
  headline?: string;
  loading: boolean;
  category?: Category;
  vibes?: Vibe[];
  sessionDurationMinutes?: number;
  maxParticipantsCount?: number;
  location?: {
    name?: string;
  };
}

interface ExperienceHeaderProps {
  experience: IExperience;
  isLoadingBrand: boolean;
  isLoadingListing: boolean;
  promoterBrandName: string;
  listing: PublicListingDetailsV3;
  hyattEligible?: {
    payEligible: boolean;
    earnEligible: boolean;
  };
}

const ExperienceHeader = ({
  experience,
  isLoadingBrand,
  isLoadingListing,
  promoterBrandName,
  listing,
  hyattEligible,
}: ExperienceHeaderProps) => {
  const { t: translate } = useTranslation();
  const limitedTime = useLimitedTimeStatus(
    listing?.limitedTime?.ticketSalesOpenAt,
    listing?.limitedTime?.ticketSalesCloseAt,
  );

  return (
    <ExperiencesHeaderContent>
      {!!limitedTime?.countdown && (
        <LimitedTimeMessage
          label={limitedTime.countdown.full}
          highlightPart={limitedTime.countdown.highlight}
        />
      )}
      {listing?.title ? (
        <MainTitles
          as={'h1' as const}
          className="experience-view-title"
          promoterBrand={!!promoterBrandName}
        >
          {listing.title}
        </MainTitles>
      ) : (
        <SkeletonLine />
      )}

      {(!experience.loading || !isLoadingListing || !isLoadingBrand) &&
        promoterBrandName && (
          <BrandName>
            {translate('by')} {promoterBrandName}
          </BrandName>
        )}

      {!experience.loading || !isLoadingListing ? (
        <Headline className="experience-view-headline">
          {listing?.summary ?? experience?.headline ?? ''}
        </Headline>
      ) : (
        <SkeletonLine />
      )}

      <TagButtonWrap
        className="experience-view__tag-buttons-container"
        aria-label="Experience tags"
        role="group"
      >
        {!isLoadingListing && listing ? (
          <ChipList className="experience-view__category-button-container">
            {listing.category?.name && (
              <CategoryChip
                as="li"
                className="experience-view__category-button"
              >
                {listing.category.name}
              </CategoryChip>
            )}
            {listing.vibes?.map((vibe: Vibe) => (
              <Chip as="li" key={vibe.name} className="experience-view__vibe">
                {vibe.name}
              </Chip>
            ))}
          </ChipList>
        ) : (
          <SkeletonLine />
        )}
      </TagButtonWrap>
      {hyattEligible && (
        <HyattExperienceBenefits
          payEligible={hyattEligible.payEligible}
          earnEligible={hyattEligible.earnEligible}
        />
      )}
    </ExperiencesHeaderContent>
  );
};

export default ExperienceHeader;
